// frontend/src/components/ui/DataTable.tsx
import React from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { DataTableMobileCard } from './DataTableMobileCard';

// Tablo Sütun Tanımı
export interface Column<T> {
  header: string;
  accessor: keyof T | ((row: T) => React.ReactNode);
  className?: string; // Sütuna özel genişlik veya hizalama için
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  isLoading?: boolean;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  actionColumn?: (row: T) => React.ReactNode;
  rowClassName?: (row: T) => string;
  mobileCardRender?: (row: T) => React.ReactNode;
}

export function DataTable<T extends { id?: number | string; urun_id?: number | string; malzeme_id?: number | string }>({
  data,
  columns,
  isLoading = false,
  onRowClick,
  emptyMessage = "Kayıt bulunamadı.",
  actionColumn,
  rowClassName,
  mobileCardRender,
}: DataTableProps<T>) {

  const getRowKey = (row: T, index: number): string | number => {
    return row.id ?? row.urun_id ?? row.malzeme_id ?? index;
  };

  if (isLoading) {
    return (
      <div className="w-full h-64 flex flex-col items-center justify-center text-gray-400 bg-white rounded-xl border border-gray-100 shadow-sm">
        <Loader2 size={32} className="animate-spin mb-2 text-primary" />
        <span className="text-sm font-medium">Veriler yükleniyor...</span>
      </div>
    );
  }

  if (!isLoading && data.length === 0) {
    return (
      <div className="w-full h-48 flex flex-col items-center justify-center text-gray-400 bg-white rounded-xl border border-gray-100 shadow-sm border-dashed">
        <AlertCircle size={32} className="mb-2 opacity-50" />
        <span className="text-sm">{emptyMessage}</span>
      </div>
    );
  }

  const tableMarkup = (
    <div className="overflow-x-auto bg-white rounded-2xl shadow-sm border border-gray-200/80">
      <table className="min-w-full divide-y divide-gray-100">
        <thead className="bg-gray-50/80">
          <tr>
            {columns.map((col, idx) => (
              <th
                key={idx}
                className={`px-6 py-3.5 text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider ${col.className || ''}`}
              >
                {col.header}
              </th>
            ))}
            {actionColumn && (
              <th className="px-6 py-3.5 text-right text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                İşlemler
              </th>
            )}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-50">
          {data.map((row, rowIdx) => (
            <tr
              key={getRowKey(row, rowIdx)}
              onClick={() => onRowClick && onRowClick(row)}
              className={`hover:bg-primary/[0.03] transition duration-150 ${onRowClick ? 'cursor-pointer' : ''} ${rowClassName ? rowClassName(row) : ''}`}
            >
              {columns.map((col, colIdx) => (
                <td key={colIdx} className={`px-6 py-4 whitespace-nowrap text-sm text-gray-700 ${col.className || ''}`}>
                  {typeof col.accessor === 'function'
                    ? col.accessor(row)
                    : (row[col.accessor] as React.ReactNode)}
                </td>
              ))}
              {actionColumn && (
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  {actionColumn(row)}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  if (!mobileCardRender) {
    // Mevcut callsite davranışı: tabloyu doğrudan render et, mobile-only div ile sarmalama yok
    return tableMarkup;
  }

  // Not: actionColumn mobile mode'da kasıtlı olarak OTOMATİK render edilmez —
  // sayfa kendi mobileCardRender JSX'i içinde aksiyonları nereye koyacağına
  // karar verir. Desktop tarafında actionColumn tableMarkup içinde aynı.
  return (
    <>
      <div data-testid="datatable-mobile-wrapper" className="md:hidden space-y-2">
        {data.map((row, rowIdx) => (
          <DataTableMobileCard
            key={getRowKey(row, rowIdx)}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
          >
            {mobileCardRender(row)}
          </DataTableMobileCard>
        ))}
      </div>
      <div data-testid="datatable-desktop-wrapper" className="hidden md:block">{tableMarkup}</div>
    </>
  );
}
